import { useCallback } from 'react';
import { LocationData, MmrState } from '../types';
import { TRANSIT_PROVIDERS, IXP_EXCHANGES } from '../constants';

interface UseMmrManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  asnPurchased: boolean;
  setAsnPurchased: React.Dispatch<React.SetStateAction<boolean>>;
  piIpPurchased: boolean;
  setPiIpPurchased: React.Dispatch<React.SetStateAction<boolean>>;
  triggerNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  gameTime: number;
}

const MMR_BUILD_COST = 25000;
const ASN_COST = 500;
const PI_IP_COST = 200;

export const useMmrManagement = ({
  locations, setLocations,
  money, setMoney,
  asnPurchased, setAsnPurchased,
  piIpPurchased, setPiIpPurchased,
  triggerNotification, gameTime,
}: UseMmrManagementProps) => {

  const buildMmr = useCallback((locationId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found.', 'error'); return; }
    if (!location.transformer) { triggerNotification('Install a Transformer before building the MMR.', 'error'); return; }
    if (!location.powerhouse?.installed) { triggerNotification('Install a Powerhouse before building the MMR.', 'error'); return; }
    if (!location.ups?.installed) { triggerNotification('Install a UPS before building the MMR.', 'error'); return; }
    if (location.mmr?.built) { triggerNotification('MMR already built at this location.', 'error'); return; }
    if (money < MMR_BUILD_COST) { triggerNotification(`Insufficient funds. MMR costs $${MMR_BUILD_COST.toLocaleString()}.`, 'error'); return; }

    setMoney(prev => prev - MMR_BUILD_COST);
    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        mmr: { built: true, builtAt: gameTime, transitProviders: [], ixpConnections: [] } as MmrState,
      },
    }));
    triggerNotification('Meet-Me Room constructed! You can now order transit providers.', 'success');
  }, [locations, money, gameTime, setMoney, setLocations, triggerNotification]);

  const purchaseAsn = useCallback(() => {
    if (asnPurchased) { triggerNotification('ASN already purchased.', 'error'); return; }
    if (money < ASN_COST) { triggerNotification(`Insufficient funds. ASN costs $${ASN_COST.toLocaleString()}.`, 'error'); return; }
    setMoney(prev => prev - ASN_COST);
    setAsnPurchased(true);
    triggerNotification('Autonomous System Number (ASN) purchased! Required for IXP peering.', 'success');
  }, [asnPurchased, money, setMoney, setAsnPurchased, triggerNotification]);

  const purchasePiIpSpace = useCallback(() => {
    if (piIpPurchased) { triggerNotification('PI IP space already purchased.', 'error'); return; }
    if (money < PI_IP_COST) { triggerNotification(`Insufficient funds. PI IP space costs $${PI_IP_COST.toLocaleString()}.`, 'error'); return; }
    setMoney(prev => prev - PI_IP_COST);
    setPiIpPurchased(true);
    triggerNotification('Provider-Independent IP space purchased! Required for IXP peering.', 'success');
  }, [piIpPurchased, money, setMoney, setPiIpPurchased, triggerNotification]);

  const orderTransit = useCallback((locationId: string, providerId: string, committedGbps: number) => {
    const location = locations[locationId];
    if (!location?.mmr?.built) { triggerNotification('Build the MMR at this location first.', 'error'); return; }
    if (!location.coreSwitch) { triggerNotification('Install a Core Switch first — transit cross-connects must terminate on the core switch.', 'warning'); return; }

    const provider = TRANSIT_PROVIDERS.find(p => p.id === providerId);
    if (!provider) { triggerNotification('Unknown transit provider.', 'error'); return; }
    if (!provider.availableLocations.includes(locationId)) {
      triggerNotification(`${provider.name} does not have a PoP at this location.`, 'error'); return;
    }

    const existing = location.mmr.transitProviders.find(tp => tp.providerId === providerId);

    if (committedGbps < provider.minCommitGbps || committedGbps > provider.maxCommitGbps) {
      triggerNotification(`Commitment must be between ${provider.minCommitGbps} and ${provider.maxCommitGbps} Gbps.`, 'error'); return;
    }

    if (existing) {
      // Adjust existing commitment
      setLocations(prev => ({
        ...prev,
        [locationId]: {
          ...prev[locationId],
          mmr: {
            ...prev[locationId].mmr!,
            transitProviders: (prev[locationId].mmr?.transitProviders ?? []).map(tp =>
              tp.providerId === providerId ? { ...tp, committedGbps } : tp
            ),
          },
        },
      }));
      triggerNotification(`${provider.name} commitment updated to ${committedGbps} Gbps.`, 'success');
      return;
    }

    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        mmr: {
          ...prev[locationId].mmr!,
          transitProviders: [
            ...(prev[locationId].mmr?.transitProviders ?? []),
            { providerId, committedGbps, orderedAt: gameTime },
          ],
        },
      },
    }));
    const monthlyCost = provider.costPerGbpsMonth * committedGbps;
    triggerNotification(`${provider.name} ${committedGbps}G ordered. Monthly cost: $${monthlyCost.toLocaleString()}/mo.`, 'success');
  }, [locations, gameTime, setLocations, triggerNotification]);

  const cancelTransit = useCallback((locationId: string, providerId: string) => {
    const location = locations[locationId];
    if (!location?.mmr) return;

    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        mmr: {
          ...prev[locationId].mmr!,
          transitProviders: (prev[locationId].mmr?.transitProviders ?? []).filter(tp => tp.providerId !== providerId),
        },
      },
    }));
    triggerNotification('Transit provider cancelled.', 'info');
  }, [locations, setLocations, triggerNotification]);

  const connectIxp = useCallback((locationId: string, ixpId: string, portSpeedGbps: number, asnOk: boolean, piIpOk: boolean) => {
    const location = locations[locationId];
    if (!location?.mmr?.built) { triggerNotification('Build the MMR at this location first.', 'error'); return; }
    if (!location.coreSwitch) { triggerNotification('Install a Core Switch first — IXP ports must terminate on the core switch.', 'warning'); return; }
    if (!asnOk) { triggerNotification('Purchase an ASN before connecting to an IXP.', 'error'); return; }
    if (!piIpOk) { triggerNotification('Purchase PI IP space before connecting to an IXP.', 'error'); return; }

    const ixp = IXP_EXCHANGES.find(x => x.id === ixpId);
    if (!ixp) { triggerNotification('Unknown IXP.', 'error'); return; }
    if (!ixp.availableLocations.includes(locationId)) {
      triggerNotification(`${ixp.name} is not available at this location.`, 'error'); return;
    }

    const existing = (location.mmr.ixpConnections ?? []).find(c => c.ixpId === ixpId);
    if (existing) { triggerNotification(`Already connected to ${ixp.name}. Disconnect first to change port.`, 'error'); return; }

    const portTier = ixp.ports.find(p => p.speedGbps === portSpeedGbps);
    if (!portTier) { triggerNotification('Invalid port speed.', 'error'); return; }

    // One-time setup fee on connect. The monthly port fee is billed via transitCostPerDay.
    if (money < portTier.setupCost) { triggerNotification(`Insufficient funds. Setup fee: $${portTier.setupCost.toLocaleString()}.`, 'error'); return; }

    setMoney(prev => prev - portTier.setupCost);
    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        mmr: {
          ...prev[locationId].mmr!,
          ixpConnections: [
            ...(prev[locationId].mmr?.ixpConnections ?? []),
            { ixpId, portSpeedGbps, connectedAt: gameTime },
          ],
        },
      },
    }));
    triggerNotification(`Connected to ${ixp.name} ${portSpeedGbps}G. Revenue bonus +${Math.round(ixp.revenueBonus * 100)}% applied.`, 'success');
  }, [locations, money, gameTime, setMoney, setLocations, triggerNotification]);

  const disconnectIxp = useCallback((locationId: string, ixpId: string) => {
    const location = locations[locationId];
    if (!location?.mmr) return;
    const ixp = IXP_EXCHANGES.find(x => x.id === ixpId);

    setLocations(prev => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        mmr: {
          ...prev[locationId].mmr!,
          ixpConnections: (prev[locationId].mmr?.ixpConnections ?? []).filter(c => c.ixpId !== ixpId),
        },
      },
    }));
    triggerNotification(`Disconnected from ${ixp?.name ?? ixpId}.`, 'info');
  }, [locations, setLocations, triggerNotification]);

  return { buildMmr, purchaseAsn, purchasePiIpSpace, orderTransit, cancelTransit, connectIxp, disconnectIxp };
};
